import { Box, Divider, Slide, Typography } from '@mui/material';
import { RichTreeView, TreeItem2 } from '@mui/x-tree-view';
import React, { Dispatch, RefObject, SetStateAction, SyntheticEvent } from 'react';
import LoadingIndicator from 'src/components/common/LoadingIndicator/LoadingIndicator';
import { EntityType, CustomerInfo, FmtpError } from '@lanterds/types';
import { HierarchyCustomer, DealerHierarchyItem, SelectedDealer, SelectedCustomer, Quantity } from '../types';
import ClearIcon from '@mui/icons-material/Clear';

type Props = {
    selectedCustomers: SelectedCustomer[];
    setSelectedCustomers: Dispatch<SetStateAction<SelectedCustomer[]>>;
    setSelectedDealers: Dispatch<SetStateAction<SelectedDealer[]>>;
    setDealers: Dispatch<SetStateAction<DealerHierarchyItem[]>>;
    isCustomersFetching?: boolean;
    customersHierarchy: HierarchyCustomer[];
    containerRef: RefObject<HTMLElement>;
    isVisible: boolean;
    fetchedCustomerData: CustomerInfo[];
};

export default function CustomerFlowTile({
    selectedCustomers,
    setSelectedCustomers,
    setSelectedDealers,
    setDealers,
    isCustomersFetching,
    customersHierarchy,
    containerRef,
    isVisible,
    fetchedCustomerData,
}: Props) {
    const isCustomerDisabledHandler = (item: HierarchyCustomer) => {
        const { customerId } = item;

        if (
            !selectedCustomers.length ||
            (customerId === Quantity.all && selectedCustomers[0].type === EntityType.ACCESS_ALL_CUSTOMERS) ||
            (customerId !== Quantity.all && selectedCustomers[0].type !== EntityType.ACCESS_ALL_CUSTOMERS)
        ) {
            return false;
        }

        return true;
    };

    const customerSelectionHandler = (event: SyntheticEvent<Element, Event>, selectedIds: string[]) => {
        if (selectedIds[0] === Quantity.all) {
            setSelectedCustomers([
                {
                    ...customersHierarchy[0],
                    id: Quantity.all,
                    type: EntityType.ACCESS_ALL_CUSTOMERS,
                } as SelectedCustomer,
            ]);
        } else {
            const mappedCustomers = selectedIds.map((id) => {
                const foundCustomer = fetchedCustomerData.find((customer: CustomerInfo) => customer.customerId === id);
                if (!foundCustomer) {
                    throw new FmtpError({ message: 'Cannot find customer.', errorCode: 'SHU005' });
                }
                return {
                    ...foundCustomer,
                    id: foundCustomer.customerId,
                    type: EntityType.CUSTOMER,
                };
            });

            setSelectedCustomers(mappedCustomers);
        }
        setSelectedDealers([]);
        setDealers([]);
    };

    const ClearSelectionsButton = () => {
        return (
            <Typography onClick={() => setSelectedCustomers([])} className="clear-button" component="div">
                <ClearIcon className="clear-icon" /> Clear
            </Typography>
        );
    };

    return (
        <Box
            className="slide-container"
            sx={{
                display: isVisible ? 'flex' : 'none',
            }}
        >
            <Slide in={isVisible} direction="right" container={containerRef.current} unmountOnExit>
                <Box className="container customer-container">
                    <Box className="header-container">
                        <Typography variant="h6" component="div" className="header">
                            Customers
                        </Typography>
                        {selectedCustomers.length ? <ClearSelectionsButton /> : <></>}
                    </Box>
                    <Divider />

                    <Box className="content-container">
                        {isCustomersFetching ? (
                            <Box className="loading-container">
                                <LoadingIndicator />
                            </Box>
                        ) : (
                            <RichTreeView
                                multiSelect={true}
                                id="customer-tree-view"
                                getItemLabel={(item: HierarchyCustomer) => item.customerName}
                                getItemId={(item: HierarchyCustomer) => item.customerId}
                                items={customersHierarchy}
                                slots={{ item: TreeItem2 }}
                                onSelectedItemsChange={customerSelectionHandler}
                                selectedItems={selectedCustomers.flatMap((customer) => customer.customerId)}
                                checkboxSelection={true}
                                isItemDisabled={isCustomerDisabledHandler}
                            />
                        )}
                    </Box>
                </Box>
            </Slide>
        </Box>
    );
}
